import {
  Box,
  Button,
  Grid2,
  TextField,
  Typography
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers";
import { Formik } from "formik";
import moment from "moment";
import React from "react";
import { useDispatch } from "react-redux";
import * as Yup from "yup";
import { updateProject } from "../../api/project";
import { useTranslationHook } from "../../hooks";
import { useFormStyles } from "../../hooks/useFormStyles";
import { setLoading } from "../../store/UI";
import { Project } from "../../types/model/project";
import { UpdateProjectRequestSchema } from "../../types/model/project/UpdateProjectRequest";

interface ProjectDetailFormProps {
  project: Project | undefined;
  onUpdate?: (project: Project) => void;
  readOnly: boolean;
}

const ProjectDetailForm = ({
  project,
  onUpdate,
  readOnly
}:ProjectDetailFormProps) => {
  const { t } = useTranslationHook();
  const dispatch = useDispatch();
  const formStyles = useFormStyles();
  const [editing, setEditing] = React.useState(false);

  const handleUpdateProject = async (values: any) => {
    try {
      const request = UpdateProjectRequestSchema.parse({
        accountId: project?.accountId,
        name: values.name,
        description: values.description,
        lat: values.lat,
        lng: values.lng,
        startDate: values.startDate,
        endDate: values.endDate,
        ownerId: project?.ownerId,
      });
      
      const response = await updateProject(project?.id!, request);
      onUpdate?.(response);
    } catch (error) {
      console.error(error);
    } finally {
      dispatch(setLoading(false));
      setEditing(false);
    }
  };

  return (
    <Box>
      <Formik
        initialValues={{
          name: project?.name || undefined,
          description: project?.description || undefined,
          lat: project?.lat || undefined,
          lng: project?.lng || undefined,
          startDate: project?.startDate || null,
          endDate: project?.endDate || null,
          field7: project?.ownerId || undefined,
        }}
        onSubmit={(values) => {
          dispatch(setLoading(true));
          handleUpdateProject(values);
        }}
        validationSchema={Yup.object().shape({
          name: Yup.string().required(
            t("admin.projects.project-name-required")
          ),
          description: Yup.string().required(
            t("admin.projects.project-description-required")
          ),
          lat: Yup.number()
            .min(-90, t("admin.projects.project-valid-lat"))
            .max(90, t("admin.projects.project-valid-lat")),
          lng: Yup.number()
            .min(-180, t("admin.projects.project-valid-lng"))
            .max(180, t("admin.projects.project-valid-lng")),
          startDate: Yup.date()
            .nullable()
            .typeError(t("admin.projects.startDate-valid")),
          endDate: Yup.date()
            .nullable()
            .typeError(t("admin.projects.endDate-valid"))
            .when("startDate", (startDate, schema) => {
              if (!startDate?.[0])
                  return schema;
              return schema.min(
                    startDate[0],
                    t("admin.projects.endDate-after-startDate")
                  );
              
            }),
        })}
      >
        {({
          values,
          touched,
          errors,
          handleChange,
          handleBlur,
          handleSubmit,
          setFieldValue,
        }) => (
          <Box component="form" onSubmit={handleSubmit} sx={{ p: 1 }}>
            <Grid2 container spacing={3}>
              <Grid2 size={12}>
                <TextField
                  fullWidth
                  required
                  variant={editing ? "outlined" : "standard"}
                  disabled={!editing}
                  sx={formStyles}
                  id="name"
                  label={t("admin.projects.name")}
                  value={values.name}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={!!(errors.name && touched.name)}
                  helperText={errors.name ? errors.name : ""}                    
                />
              </Grid2>

              {/* LOCATION */}
              <Grid2 size={6}>
                <Typography variant="h6" color="text.secondary" sx={{ fontWeight: 700, mb: 2 }}>
                  {t("projects.location")}
                </Typography>

                <Grid2 container spacing={2}>
                  <Grid2 size={6}>
                    <TextField
                      fullWidth
                      disabled={!editing}
                      variant={editing ? "outlined" : "standard"}
                      id="lat"
                      label={t("admin.projects.lat")}
                      value={values.lat}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={!!(errors.lat && touched.lat)}
                      helperText={touched.lat && errors.lat}
                      sx={formStyles}
                    />
                  </Grid2>

                  <Grid2 size={6}>
                    <TextField
                      fullWidth
                      disabled={!editing}
                      variant={editing ? "outlined" : "standard"}
                      id="lng"
                      label={t("admin.projects.lng")}
                      value={values.lng}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={!!(errors.lng && touched.lng)}
                      helperText={touched.lng && errors.lng}
                      sx={formStyles}
                    />
                  </Grid2>
                </Grid2>
              </Grid2>

              {/* DATES */}
              <Grid2 size={6}>
                <Typography variant="h6" color="text.secondary" sx={{ fontWeight: 700, mb: 2 }}>
                  {t("projects.dates")}
                </Typography>

                <Grid2 container spacing={2}>
                  <Grid2 size={6}>
                    <DatePicker
                      label={t("admin.projects.start-date")}
                      disabled={!editing}
                      value={
                        values.startDate
                          ? moment(values.startDate)
                          : null
                      }
                      onChange={(value) =>
                        setFieldValue(
                          "startDate",
                          value ? value.toDate() : null
                        )
                      }
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          sx: formStyles,
                        },
                      }}
                    />
                  </Grid2>

                  <Grid2 size={6}>
                    <DatePicker
                      label={t("admin.projects.end-date")}
                      disabled={!editing}
                      value={
                        values.endDate
                          ? moment(values.endDate)
                          : null
                      }
                      onChange={(value) =>
                        setFieldValue(
                          "endDate",
                          value ? value.toDate() : null
                        )
                      }
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          sx: formStyles,
                        },
                      }}
                    />
                  </Grid2>
                </Grid2>
              </Grid2>

              <Grid2 size={12}>
                <TextField
                  fullWidth
                  required
                  multiline
                  rows={4}
                  disabled={!editing}
                  variant={editing ? "outlined" : "standard"}
                  id="description"
                  label={t("admin.projects.description")}
                  value={values.description}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={!!(errors.description && touched.description)}
                  helperText={touched.description && errors.description}
                  sx={formStyles}
                />
              </Grid2>

              {/* ACTIONS */}
              {!readOnly && (
                <Grid2
                  size={12}
                  sx={{
                    display: "flex",
                    justifyContent: "flex-end",
                    gap: 2,
                    mt: 3,
                  }}
                >
                  {editing ? (
                    <>
                      <Button
                        variant="outlined"
                        color="secondary"
                        onClick={() => setEditing(false)}
                      >
                        {t("generic.cancel")}
                      </Button>
                      <Button type="submit" variant="contained">
                        {t("generic.save")}
                      </Button>
                    </>
                  ) : (
                    <Button variant="contained" onClick={() => setEditing(true)}>
                      {t("generic.edit")}
                    </Button>
                  )}
                </Grid2>
              )}
            </Grid2>
          </Box>
        )}
      </Formik>
    </Box>
  );
};

export default ProjectDetailForm;
